import { ref, unref, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { announce } from "@/composables/useLiveAnnouncer"

/**
 * Updates a visually hidden aria-live region when vue-select shows "no matching options".
 * The default slot content sits inside `ul[role=listbox]` and is often not announced; this
 * mirrors the empty-filter state outside the listbox (same approach as CustomSelect).
 *
 * @param {import('vue').Ref} vselectRef - ref on the vue-select component instance (`<VSelect ref="…">`)
 * @param {string} fieldLabel - Short name for the field (e.g. "Role") prepended to the message
 * @returns {{ message: import('vue').Ref<string>, liveKey: import('vue').Ref<number>, sync: () => void, clear: () => void }}
 */
export function useVueSelectNoMatchAnnouncement(vselectRef, fieldLabel) {
  const { t } = useI18n()
  const message = ref("")
  const liveKey = ref(0)

  const sync = () => {
    nextTick(() => {
      const vs = unref(vselectRef)
      if (!vs?.open) {
        message.value = ""
        return
      }
      const filtered = vs.filteredOptions
      const len = Array.isArray(filtered) ? filtered.length : 0
      if (len > 0) {
        message.value = ""
        return
      }
      const msg = t("common.sorry_no_matching_options")
      const label = typeof fieldLabel === "string" ? fieldLabel.trim() : ""
      const fullMessage = label ? `${label}. ${msg}` : msg
      message.value = fullMessage
      liveKey.value += 1
      /* Keyed sibling region is recreated on each change, so announce reliably via the
         persistent global live region instead. `force` so repeated identical
         "no matching options" still re-announces on NVDA (Deque 2189100 / 2174531). */
      announce(fullMessage, { force: true })
    })
  }

  const clear = () => {
    message.value = ""
  }

  return { message, liveKey, sync, clear }
}
